// The semantic-search `answer` arrives as free-form LLM prose with no
// structural markup (see backend cosmos_service: the model is only told
// "Answer using context"). To satisfy the ticket's "support numbered /
// sectioned / raw text" requirement we parse that prose line-by-line into a
// small set of renderable blocks. This is deliberately heuristic — there is no
// guaranteed structure to rely on.

export type AnswerBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; text: string }
  | { type: "ordered-list"; items: string[] };

// "1. ", "2) " etc. at the start of a line.
const NUMBERED_LINE = /^\s*(\d+)[.)]\s+(.*)$/;

// A short line that is purely a label terminated by a colon, e.g.
// "Clinical impression:". A colon in the middle of a sentence (e.g.
// "Clinical impression: mechanical back pain.") is NOT a heading.
const isHeading = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed.endsWith(":")) return false;
  const label = trimmed.slice(0, -1).trim();
  // <=60 chars: a heading is a short label, not a full sentence ending in a colon.
  return label.length > 0 && label.length <= 60 && !label.includes(":");
};

const isNumbered = (line: string): boolean => NUMBERED_LINE.test(line.trim());

export const parseAnswer = (answer: string): AnswerBlock[] => {
  const lines = answer.split("\n");
  const blocks: AnswerBlock[] = [];
  let currentList: string[] | null = null;

  const flushList = () => {
    if (currentList && currentList.length > 0) {
      blocks.push({ type: "ordered-list", items: currentList });
    }
    currentList = null;
  };

  // Index of the next non-blank line, used to disambiguate a colon-terminated
  // line: a heading precedes prose, whereas an intro lead-in precedes a list.
  const nextNonBlank = (from: number): string | undefined => {
    for (let i = from; i < lines.length; i++) {
      const t = lines[i].trim();
      if (t) return t;
    }
    return undefined;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    const numbered = trimmed.match(NUMBERED_LINE);
    if (numbered) {
      if (!currentList) currentList = [];
      currentList.push(numbered[2].trim());
      return;
    }

    flushList();

    // A colon-terminated label is a heading only when it introduces prose. If
    // the next line is a numbered item, it's an intro lead-in to the list.
    const next = nextNonBlank(index + 1);
    if (isHeading(trimmed) && !(next && isNumbered(next))) {
      blocks.push({ type: "heading", text: trimmed.slice(0, -1).trim() });
      return;
    }

    blocks.push({ type: "paragraph", text: trimmed });
  });

  flushList();
  return blocks;
};
